import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Head from 'next/head';

import { publish } from '@/utils/publish';
import { webpageContactRequest } from '@/store/actions/public.actions';

import { buildPalette } from './utils/palette';
import { CF360_SCOPED_CSS } from './utils/scopedStyles';
import { DEFAULT_CF360_WEBPAGE_DISCLAIMER } from './utils/disclaimer';
import { getAgentTitle, getPhoneNumber } from './utils/formatters';
import {
  sentences,
  stripHtml,
  nonEmpty,
  initials,
  buildHoursRows,
  mapsUrl,
  mapEmbedUrl,
  readTrackingParams
} from './utils/content';

import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { About } from './components/About';
import { Products } from './components/Products';
import { Carriers } from './components/Carriers';
import { FindAgent } from './components/FindAgent';
import { Connect } from './components/Connect';
import { FindMe } from './components/FindMe';
import { Footer } from './components/Footer';
import { GetInTouchModal } from './components/GetInTouchModal';

/**
 * WebpageCF360 — the ClientWave360 public agent/company landing page.
 *
 * REQUEST-74 redesign: centered gradient hero, About Me, Products & Services
 * icon grid, Carriers I Represent, Let's Connect (form + Calendly), Find Me
 * contact/hours/map, and — on agency company pages — Find an Agent. Sections
 * collapse when their data is empty; branding flows from the corporation's
 * colors (defaulting to ClientWave360 green). All styling is scoped under
 * `[data-cf360]` so it never affects other tenants. Rendered only when
 * `config().CLIENTFLOW360` is active.
 */
// `info` overrides the Redux `public.info` payload — the toll-free wizard
// renders this page off-screen from an authenticated fetch to screenshot it.
export default function WebpageCF360({ webpageUsername, isCompanyWebpage, info: infoOverride }) {
  const { info: storeInfo } = useSelector(state => state.public);
  const info = infoOverride || storeInfo;
  const containerRef = useRef(null);
  const [contactOpen, setContactOpen] = useState(false);

  const data = useMemo(() => normalize(info, isCompanyWebpage), [info, isCompanyWebpage]);

  // Agent pages brand from their corporation; company pages carry their own colors at the root.
  const brand = isCompanyWebpage ? info : info?.corporation;
  const paletteVars = useMemo(
    () => buildPalette(brand?.color_1, brand?.color_2, brand?.color_3),
    [brand?.color_1, brand?.color_2, brand?.color_3]
  );

  // Capture UTM params from the landing URL once, so they survive in-page
  // navigation (hash links) and are attached to the contact on submit.
  const trackingRef = useRef({});
  useEffect(() => {
    trackingRef.current = readTrackingParams();
  }, []);

  const handleSubmit = useCallback(
    async body => {
      try {
        await webpageContactRequest({ domain: webpageUsername, body: { ...body, ...trackingRef.current } })();
        publish('success', 'Your request was successfully sent.');
      } catch (ex) {
        publish('error', ex);
        throw ex;
      }
    },
    [webpageUsername]
  );

  const openContact = useCallback(() => setContactOpen(true), []);
  const closeContact = useCallback(() => setContactOpen(false), []);

  // Reveal-on-scroll: add `.in` to `.reveal` elements as they enter the viewport.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;
    const revealed = root.querySelectorAll('.reveal');
    if (!('IntersectionObserver' in window)) {
      revealed.forEach(el => el.classList.add('in'));
      return undefined;
    }
    const io = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    revealed.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [data]);

  if (!data) return null;

  const navItems = [
    data.aboutParagraphs.length && { href: '#about', label: 'About' },
    data.plans.length && { href: '#products', label: 'Products & Services' },
    data.carriers.length && { href: '#carriers', label: 'Carriers' },
    data.agents.length && { href: '#agents', label: 'Find an Agent' },
    { href: '#contact', label: 'Contact' }
  ].filter(Boolean);

  const containerStyle = {
    all: 'initial',
    display: 'block',
    position: 'relative',
    width: '100%',
    minHeight: '100vh',
    margin: 0,
    padding: 0,
    overflowX: 'hidden',
    fontFamily: 'var(--font-text)',
    fontSize: '16px',
    lineHeight: 1.6,
    color: 'var(--text-body)',
    background: '#fff',
    WebkitFontSmoothing: 'antialiased',
    textRendering: 'optimizeLegibility',
    ...paletteVars
  };

  return (
    <>
      <Head>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Head>
      <style dangerouslySetInnerHTML={{ __html: CF360_SCOPED_CSS }} />

      <div ref={containerRef} data-cf360="" style={containerStyle}>
        <Header
          logoUrl={data.logoUrl}
          wordmark={data.wordmark}
          navItems={navItems}
          phone={data.phone}
          onGetInTouch={openContact}
        />

        <main id="top">
          <Hero
            name={data.name}
            title={data.title}
            company={data.company}
            addressLine={data.addressLine}
            phone={data.phone}
            photoUrl={data.photoUrl}
            monogram={data.monogram}
            onGetInTouch={openContact}
          />

          <About paragraphs={data.aboutParagraphs} />

          <Products plans={data.plans} />

          <Carriers carriers={data.carriers} />

          <FindAgent agents={data.agents} />

          <Connect
            name={data.name}
            firstName={data.firstName}
            calendlyHref={data.calendlyHref}
            onSubmit={handleSubmit}
          />

          <FindMe
            isCompany={isCompanyWebpage}
            addressLines={data.addressLines}
            phone={data.phone}
            email={data.email}
            languages={data.languages}
            hoursRows={data.hoursRows}
            mapEmbedSrc={data.mapEmbedSrc}
            mapsHref={data.mapsHref}
          />
        </main>

        <Footer name={data.name} socialLinks={data.socialLinks} disclaimer={data.disclaimer} />

        <GetInTouchModal
          open={contactOpen}
          onClose={closeContact}
          name={data.name}
          firstName={data.firstName}
          onSubmit={handleSubmit}
        />
      </div>
    </>
  );
}

/**
 * Normalize the platform's `public.info` payload (individual agent OR company
 * webpage) into the flat shape the section components consume. Returns null when
 * there is nothing to render.
 */
function normalize(info, isCompanyWebpage) {
  if (!info) return null;
  const corp = info.corporation || {};

  const fields = isCompanyWebpage
    ? {
        firstName: info.name,
        lastName: '',
        webpageTitle: '',
        aboutMe: info.description,
        photoUrl: info.logo?.url,
        email: info.office_email,
        phone: getPhoneNumber({ phone: info.office_phone }),
        address_1: info.office_address,
        address_2: '',
        city: info.office_city,
        state: info.office_state,
        zip_code: info.office_zip_code,
        officeHours: info.office_hours,
        languages: info.languages_spoken,
        plans: [],
        carriers: [],
        disclaimer: '',
        social: {
          facebook: info.facebook,
          twitter: info.twitter,
          instagram: info.instagram,
          linkedin: info.linkedin,
          youtube: info.youtube
        }
      }
    : pickProfile(info);

  if (!isCompanyWebpage && !info.profile) return null;

  const name = [fields.firstName, fields.lastName].filter(Boolean).join(' ');
  const corpName = corp.name;
  const agentTitle = (fields.webpageTitle || '').trim() || getAgentTitle(fields.title) || '';

  // Group the bio into short paragraphs of two sentences each.
  const aboutText = stripHtml(fields.aboutMe);
  const allSentences = sentences(aboutText).map(s => s.trim());
  const aboutParagraphs = [];
  for (let i = 0; i < allSentences.length; i += 2) {
    aboutParagraphs.push(allSentences.slice(i, i + 2).join(' '));
  }

  const addr = {
    address_1: fields.address_1,
    address_2: fields.address_2,
    city: fields.city,
    state: fields.state,
    zip_code: fields.zip_code
  };
  const addressLineOne = [fields.address_1, fields.address_2].filter(Boolean).join(', ');
  const addressLineTwo = [[fields.city, fields.state].filter(Boolean).join(', '), fields.zip_code]
    .filter(Boolean)
    .join(' ');
  const addressLines = addressLineOne ? [addressLineOne, addressLineTwo].filter(Boolean) : [];

  const calendly = (fields.calendlyHref || '').trim();
  const calendlyHref = calendly && calendly.charAt(0) !== '#' ? calendly : null;

  // Medicare compliance: fall back to the standard disclaimer when none is set.
  const disclaimer =
    typeof fields.disclaimer === 'string' && fields.disclaimer.trim()
      ? fields.disclaimer.trim()
      : DEFAULT_CF360_WEBPAGE_DISCLAIMER;

  return {
    // header
    logoUrl: isCompanyWebpage ? info.logo?.url : corp.logo?.url,
    wordmark: isCompanyWebpage ? name : corpName || name,
    // hero
    name,
    firstName: fields.firstName,
    title: isCompanyWebpage ? '' : agentTitle,
    company: isCompanyWebpage ? '' : corpName,
    addressLine: addressLines.join(', '),
    phone: fields.phone,
    // Company pages already show the logo in the header — no hero portrait.
    photoUrl: isCompanyWebpage ? null : fields.photoUrl,
    monogram: isCompanyWebpage ? '' : initials(fields.firstName, fields.lastName),
    // sections
    aboutParagraphs,
    plans: nonEmpty(fields.plans),
    carriers: nonEmpty(fields.carriers),
    agents: isCompanyWebpage ? info.agents || [] : [],
    calendlyHref,
    // find me
    email: fields.email,
    languages: nonEmpty(fields.languages),
    hoursRows: buildHoursRows(fields.officeHours),
    addressLines,
    mapEmbedSrc: fields.address_1 ? mapEmbedUrl(addr) : null,
    mapsHref: mapsUrl(addr),
    // footer
    socialLinks: fields.social,
    disclaimer
  };
}

/** Extract the fields used by the page from an individual agent profile. */
function pickProfile(info) {
  const p = info.profile || {};
  const effectivePlans = p.cf360_plans_offered?.length ? p.cf360_plans_offered : p.plans_offered?.split?.(';') || [];

  return {
    firstName: p.first_name,
    lastName: p.last_name,
    webpageTitle: p.webpage_title,
    title: p.title,
    aboutMe: p.about_me,
    photoUrl: p.personal_photo?.url,
    email: info.email,
    phone: getPhoneNumber({ cell_phone: p.cell_phone, phone: p.phone, phone_ext: p.phone_ext }),
    address_1: p.address_1,
    address_2: p.address_2,
    city: p.city,
    state: p.state,
    zip_code: p.zip_code,
    officeHours: p.office_hours,
    languages: p.languages_spoken,
    plans: effectivePlans,
    carriers: p.cf360_carriers,
    calendlyHref: p.calendly_link || p.calendly_embed_link,
    disclaimer: p.webpage_disclaimer,
    social: {
      facebook: p.facebook,
      twitter: p.twitter,
      instagram: p.instagram,
      linkedin: p.linkedin,
      youtube: p.youtube
    }
  };
}
